import React, { useState } from 'react'
import { useHistory, Link } from 'react-router-dom'
import ReactSelect from 'react-select'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { DEFAULT_COUNTRY_CODE } from 'lib/core/resources/constants'
import {
  useCreateAccount,
  useLogin,
  useDirectoryOptions
} from 'lib/core/hooks'
import { Button, Form, Alert } from 'lib/ui/components'
import { GENDER_OPTIONS } from 'app/livbrite/resources/constants'
import {
  StyledForm,
  Row,
  Actions,
  StyledSelect
} from 'app/livbrite/components/StyledForm'
import { Title } from 'app/livbrite/scenes/Customer/components/Styled'
import { schema, validation, FormValues } from './schema'
import { PublicLayout } from 'app/livbrite/components/PublicLayout'
import { useQuery } from '@apollo/client'
import { communityListData } from 'app/livbrite/services/community'

interface Prop {
  avoidRedirect?: boolean
}

interface Community {
  community_id: Number
  name: String
  parent_id: Number
  community_code: String
  community_address: String
  community_name: [String]
}

export const CreateAccount = ({ avoidRedirect }: Prop) => {
  const intl = useIntl()
  const history = useHistory()

  const { regionOptions, countryInfoIsFetching } = useDirectoryOptions({
    countryCode: DEFAULT_COUNTRY_CODE
  })

  const [subscribe, setSubscribe] = useState(false)
  const [communityItems, setCommunityItems] = useState<Community[] | undefined>()
  const { loading: communityLoading, data } = useQuery(communityListData)
  const {
    createAccount,
    isFetching: createAccountIsFetching,
    errorCode
  } = useCreateAccount()
  const communityListItems: Community[] = data?.getCommunityList.items
  const communityList = communityListItems?.reduce(
    (obj: any[], community: Community) => {
      if (community.parent_id) {
        obj.push({
          label: community.name,
          value: community.community_id
        })
      }
      return obj
    },
    []
  )

  const communityOptions = [
    {
      label: 'N/A',
      value: 'false'
    },
    {
      label: 'Community Not Listed',
      value: 'communityNotListed'
    },
    ...(communityList ?? [])
  ]

  const [community, setCommunity] = useState(communityOptions[0])
  const [accountType, setAccountType] = useState<string | undefined>()
  const isResident = accountType === 'resident'
  const isEmployee = accountType === 'employee'
  const { login, isFetching: loginIsFetching } = useLogin({
    onSuccess: () => {
      if (avoidRedirect) {
        return
      }
      history.push('/customer/account')
    }
  })
  const isCustomCommunity = community.value === 'communityNotListed'

  const handleSubmit = (values: FormValues) => {
    const isEmployee = values.accountType === 'employee'
    const isCommunityNotListed = values.communitySelect === 'communityNotListed'
    const communityName = values.communityName?.filter((n) => n)
    const extensionAttr = {
      extensionAttributes: {
        customer_type: isEmployee ? 1 : 0,
        community_code: isEmployee ? values.communityCode : '',
        community_name: isEmployee
          ? communityName
          : !isCommunityNotListed
            ? [community.label]
            : [values.community]
      }
    }

    const selectedRegionInfo = regionOptions.find(
      (region) => region.value === values.state
    )
    const regionId = selectedRegionInfo?.id

    const address = {
      city: values.city,
      country_id: DEFAULT_COUNTRY_CODE,
      default_shipping: true,
      default_billing: true,
      firstname: values.firstName,
      lastname: values.lastName,
      postcode: values.zipCode,
      region_id: regionId,
      street: [values.street],
      telephone: values.telephone
    }

    const birthDate = values.birthYear

    createAccount(
      {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
        gender: parseInt(values.gender),
        dob: birthDate,
        ...extensionAttr,
        address
      },
      {
        callback: {
          onSuccess: () => {
            login({
              email: values.email,
              password: values.password,
              persistent: Boolean(process.env.REACT_APP_PERSISTENT_LOGIN)
            })
          }
        }
      }
    )
  }

  const isFetching = createAccountIsFetching || loginIsFetching

  return (
    <PublicLayout className='general-content'>
      <div>
        <Title>
          <FormattedMessage id='customer.account.create.title' />
        </Title>

        <Formik
          initialValues={schema}
          validationSchema={validation(intl)}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, values, isValid }) => {
            return (
              <Form.Wrapper>
                <StyledForm>
                  <Row>
                    <StyledSelect>
                      <label>
                        Please select one
                        <em>*</em>
                      </label>
                      <Field
                        name='accountType'
                        component={Form.Select}
                        options={accountTypeOptions}
                        interceptChange={(e: any) => {
                          setAccountType(e.value)
                        }}
                      />
                    </StyledSelect>
                  </Row>

                  {!communityLoading && isResident && (
                    <Row>
                      <StyledSelect bottomSpace>
                        <label>
                          {intl.formatMessage({
                            id: 'customer.account.create.label.community.select'
                          })}
                          <em>*</em>
                        </label>
                        <ReactSelect
                          name='communitySelect'
                          classNamePrefix={
                            Form.ReactSelectTheme.classNamePrefix
                          }
                          options={communityOptions}
                          onChange={(e: any) => {
                            const communitySelected = communityOptions.find(
                              (option) => option.value === e.value
                            )
                            setCommunity(
                              communitySelected ?? communityOptions[0]
                            )
                            setFieldValue('communitySelect', e.value)
                          }}
                        />
                      </StyledSelect>
                      {isCustomCommunity && (
                        <Field
                          name='community'
                          label={intl.formatMessage({
                            id: 'customer.account.create.label.community'
                          })}
                          component={Form.Input}
                          isRequired
                        />
                      )}
                    </Row>
                  )}
                  {isEmployee && (
                    <Row>
                      <Field
                        name='communityCode'
                        label={intl.formatMessage({
                          id: 'customer.account.create.label.community.code'
                        })}
                        component={Form.Input}
                        isRequired
                        interceptChange={(e: any) => {
                          const items = communityListItems?.filter(
                            (community: Community) =>
                              community.parent_id &&
                              community.community_code === e.value
                          )
                          setCommunityItems(items)
                        }}
                      />
                      <div>
                        <label>Community name</label>
                        <div>
                          {communityItems && (
                            <>
                              {communityItems?.length > 0 ? (
                                <>
                                  {communityItems?.map(
                                    (community: Record<string, any>) => {
                                      return (
                                        <label
                                          className='checkbox'
                                          key={community.community_id}
                                        >
                                          <Field
                                            type='checkbox'
                                            name='communityName'
                                            value={community.name}
                                            checked={values.communityName?.includes(
                                              community.name
                                            )}
                                          />
                                          <span>{community.name}</span>
                                        </label>
                                      )
                                    }
                                  )}
                                </>
                              ) : (
                                <Alert type='danger'>
                                  <span>
                                    No communities match this code. Please check
                                    your code and if problem persists, reach out
                                    to us at{' '}
                                    <a href='mailto:hello@livbritetoday.com'>
                                      hello@livbritetoday.com
                                    </a>
                                    .
                                  </span>
                                </Alert>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </Row>
                  )}
                  <Row>
                    <div>
                      <Field
                        name='firstName'
                        label={intl.formatMessage({
                          id: 'customer.account.create.label.firstName'
                        })}
                        component={Form.Input}
                        isRequired
                        autoFocus
                      />
                    </div>
                    <div>
                      <Field
                        name='lastName'
                        label={intl.formatMessage({
                          id: 'customer.account.create.label.lastName'
                        })}
                        component={Form.Input}
                        isRequired
                      />
                    </div>
                  </Row>

                  <Row>
                    <div className='bottom__checkbox'>
                      <Field
                        name='email'
                        label={intl.formatMessage({
                          id: 'customer.account.create.label.email'
                        })}
                        component={Form.Input}
                        isRequired
                      />
                      <label>
                        <input
                          type='checkbox'
                          checked={subscribe}
                          onChange={(e) => setSubscribe(e.target.checked)}
                        />
                        <span>I agree to receive emails from LivBrite</span>
                      </label>
                    </div>

                    <Field
                      name='telephone'
                      label={intl.formatMessage({
                        id: 'customer.account.create.label.phone'
                      })}
                      component={Form.Input}
                      isRequired
                    />
                  </Row>

                  <Row>
                    <Field
                      name='password'
                      label={intl.formatMessage({
                        id: 'customer.account.create.label.password'
                      })}
                      component={Form.Input}
                      type='password'
                      isRequired
                    />
                    <div className='inline'>
                      <StyledSelect>
                        <label>
                          {intl.formatMessage({
                            id: 'customer.account.create.label.birth'
                          })}
                        </label>
                        <Field
                          name='birthYear'
                          component={Form.Select}
                          options={yearOptions}
                        />
                      </StyledSelect>
                    </div>
                  </Row>

                  <Row>
                    <Field
                      name='passwordRetype'
                      label={intl.formatMessage({
                        id: 'customer.account.create.label.passwordRetype'
                      })}
                      component={Form.Input}
                      type='password'
                      isRequired
                    />
                    <StyledSelect>
                      <label>
                        {intl.formatMessage({
                          id: 'customer.account.create.label.gender'
                        })}
                      </label>
                      <Field
                        name='gender'
                        component={Form.Select}
                        options={genderOptions}
                      />
                    </StyledSelect>
                  </Row>

                  <Field
                    name='street'
                    label={intl.formatMessage({
                      id: 'customer.account.create.label.address'
                    })}
                    component={Form.Input}
                    isRequired
                  />

                  <Row className='address'>
                    <Field
                      name='city'
                      label={intl.formatMessage({
                        id: 'customer.account.create.label.city'
                      })}
                      component={Form.Input}
                      isRequired
                    />

                    <StyledSelect>
                      <label>
                        {intl.formatMessage({
                          id: 'customer.account.create.label.state'
                        })}
                        <em>*</em>
                      </label>
                      <Field
                        name='state'
                        component={Form.Select}
                        options={regionOptions}
                        isLoading={countryInfoIsFetching}
                        isRequired
                      />
                    </StyledSelect>

                    <Field
                      name='zipCode'
                      label={intl.formatMessage({
                        id: 'customer.account.create.label.zip'
                      })}
                      component={Form.Input}
                      isRequired
                    />
                  </Row>

                  {!isFetching && errorCode && (
                    <Alert type='danger'>
                      <FormattedMessage
                        id={errorCode}
                        values={{
                          loginLink: (str: string) => (
                            <Link to='/login'>{str}</Link>
                          ),
                          resetPasswordLink: (str: string) => (
                            <Link to='/customer/account/forgotpassword'>
                              {str}
                            </Link>
                          )
                        }}
                      />
                    </Alert>
                  )}
                  <Actions alignRight>
                    <div>
                      <span>
                        By clicking create account, I agree to the{' '}
                        <a href='/terms-and-conditions' target='_blank'>
                          terms and conditions.
                        </a>
                      </span>
                    </div>
                    <Button
                      type='submit'
                      isLoading={isFetching}
                      isDisabled={!isValid}
                    >
                      <FormattedMessage id='action.createAccount' />
                    </Button>
                  </Actions>
                </StyledForm>
              </Form.Wrapper>
            )
          }}
        </Formik>
      </div>
    </PublicLayout>
  )
}

interface Option {
  label: string
  value: string
}

const yearOptions = getYearsOptions()
const genderOptions = GENDER_OPTIONS

function getYearsOptions (): Option[] {
  const options = []
  const lastYear = new Date().getFullYear()
  const minimunAge = 20
  const maxAge = 100
  for (let i = lastYear - maxAge; i <= lastYear - minimunAge; i++) {
    const year = i.toString()
    options.push({
      value: year.substr(-2),
      label: year
    })
  }
  options.unshift({
    value: '',
    label: ''
  })
  return options
}

const accountTypeOptions = [
  {
    label: '',
    value: ''
  },
  {
    label: 'I am a retirement community resident',
    value: 'resident'
  },
  {
    label: 'I am a retirement community employee',
    value: 'employee'
  },
  {
    label: 'Neither',
    value: 'other'
  }
]
